/**
 * Angular's `HttpParams` becomes a query string; the shape of the request is unchanged.
 *
 * One definition rather than one per store. `home.store.ts` held the first copy and
 * `container.store.ts` was about to hold the second, which is the duplication this repository has a
 * guard for — a copy drifts silently, and the drift here would be *which requests get which
 * parameters*, the exact thing several specs assert by inspecting the URL.
 *
 * **It percent-encodes, and that is a correction rather than a preference.** The obvious
 * implementation is `new URLSearchParams(...).toString()`, which is what this was at first — but
 * `URLSearchParams` is `application/x-www-form-urlencoded`, so it writes a space as `+` where
 * Angular's `HttpParams` wrote `%20`. For every parameter this application had when the helper was
 * extracted the two agree exactly, because they are numeric ids and closed-vocabulary view names.
 * The `/tags/:name` port is where they stop agreeing: the tag term is the first
 * PRACTITIONER-SUPPLIED string to reach a query parameter, and `e2e/tags-view.spec.ts` drives a tag
 * literally named `work stuff`.
 *
 * Both encodings are legal in a query string and every mainstream server decodes both, so this is
 * not a bug being fixed — it is a difference from the thing being ported, found before it could
 * matter. `%20` is the one that needs no assumption about someone else's parser, and this cycle's
 * locked constraint is that safety beats cost. `/search`'s query string is the next
 * practitioner-supplied value due to arrive here, so the decision is worth making once, in the one
 * place, rather than per caller.
 *
 * `encodeURIComponent` is applied to the KEY as well as the value. No key in this application needs
 * it today; leaving it off would make the helper correct only for the keys it happens to have seen.
 */
export const withQuery = (url: string, params: Record<string, string | number>): string => {
  const search = Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
  return `${url}?${search}`;
};
